import { CadenceSchedulePreview } from '../components/CadenceSchedulePreview';
import { CatchballDiagram, pathToDiagramTrack } from '../components/CatchballDiagram';
import { buildOrgById } from '../lib/pathBuilder';
import { runPreLaunchChecks } from '../lib/validation';
import { pathModeOf } from '../lib/customPathModel';
import type { CycleDesignerFormState, DesignerPath, OrgPathNode } from '../types';

interface StepReviewLaunchProps {
  form: CycleDesignerFormState;
  paths: DesignerPath[];
  orgNodes: OrgPathNode[];
  maxRelativeLevel: number;
  launching: boolean;
  launchError: string | null;
  onLaunch: () => void;
}

export function StepReviewLaunch({
  form,
  paths,
  orgNodes,
  maxRelativeLevel,
  launching,
  launchError,
  onLaunch,
}: StepReviewLaunchProps) {
  const orgById = buildOrgById(orgNodes);
  const { checks, allHardPass } = runPreLaunchChecks(paths, orgNodes, pathModeOf(form));

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0, overflow: 'hidden' }}>
      <div style={{ flex: 1, overflow: 'auto', padding: '24px 32px' }}>
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.68rem', letterSpacing: '0.14em', color: 'var(--mustard)', textTransform: 'uppercase' }}>Ready to Launch</div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: '1.6rem', fontWeight: 300, color: 'var(--text-pri)', margin: '8px 0' }}>{form.cycleName}</h2>
          <div style={{ fontSize: '0.95rem', color: 'var(--text-ter)' }}>
            {paths.length} Track{paths.length !== 1 ? 's' : ''}
          </div>
        </div>

        <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-dim)', textTransform: 'uppercase', marginBottom: 10 }}>Ball Paths</div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 24 }}>
          {paths.map((path) => (
            <div key={path.id} style={{ borderLeft: `3px solid ${path.color}`, padding: '12px 14px', background: 'var(--bg-raised)', border: '1px solid var(--rule)', borderLeftWidth: 3, borderLeftColor: path.color }}>
              <CatchballDiagram
                track={pathToDiagramTrack(path.name, path.color, path.nodes, orgById, maxRelativeLevel)}
                mode="preview"
              />
              {path.params.guidance && (
                <div style={{ marginTop: 8, fontSize: '0.9rem', color: 'var(--text-ter)', fontStyle: 'italic' }}>
                  &ldquo;{path.params.guidance}&rdquo;
                </div>
              )}
            </div>
          ))}
        </div>

        <CadenceSchedulePreview form={form} paths={paths} orgNodes={orgNodes} />

        {/* Only what's actually wrong. A checklist of permanently-green rows is
            noise, and it trains people to stop reading the one row that matters. */}
        {!allHardPass && (
          <>
            <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--coral)', textTransform: 'uppercase', marginBottom: 10 }}>Not ready to launch</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {checks.filter((c) => !c.ok).map((c) => (
                <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: '0.95rem', color: 'var(--coral)' }}>
                  <span>✕</span>
                  {c.label}
                </div>
              ))}
            </div>
            <div style={{ marginTop: 12, fontSize: '0.9rem', color: 'var(--text-dim)' }}>
              Fix this in Design Paths, then come back.
            </div>
          </>
        )}
      </div>

      <aside style={{ width: 260, flexShrink: 0, borderLeft: '1px solid var(--rule)', padding: 20, background: 'var(--bg-raised)', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 12 }}>Launch</div>
          <button
            type="button"
            disabled={!allHardPass || launching}
            onClick={onLaunch}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: allHardPass ? 'var(--green)' : 'var(--rule-bright)',
              color: allHardPass ? 'var(--bg)' : 'var(--text-dim)',
              border: 'none',
              fontFamily: 'var(--mono)',
              fontSize: '0.8rem',
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              cursor: allHardPass && !launching ? 'pointer' : 'not-allowed',
            }}
          >
            {launching ? 'Launching…' : 'Launch Cycle'}
          </button>
          <p style={{ fontSize: '0.95rem', color: 'var(--text-dim)', marginTop: 8, lineHeight: 1.45 }}>
            Creates the tracks and opens the cycle's page, where its settings and tracks are.
          </p>
          {launchError && (
            <p style={{ fontSize: '0.9rem', color: 'var(--coral)', marginTop: 8 }}>{launchError}</p>
          )}
        </div>

        <div>
          <div style={{ fontFamily: 'var(--mono)', fontSize: '0.65rem', letterSpacing: '0.14em', color: 'var(--text-ter)', textTransform: 'uppercase', marginBottom: 10 }}>Summary</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: '0.95rem', color: 'var(--text-sec)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Tracks</span><strong>{paths.length}</strong></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span>Total stops</span><strong>{paths.reduce((s, p) => s + p.nodes.length, 0)}</strong></div>
          </div>
        </div>
      </aside>
    </div>
  );
}
